import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector, useStore } from 'react-redux';
import UploadDocuments from "../components/Dashboard/UploadDocuments";
import CompanyInformation from "../components/Dashboard/CompanyInformation";
import { 
  setStepsCompleted, 
  setShowBackToDashboardButton, 
  prepareTaricData, 
  generateTaricJson, 
  toggleCollapseFiles,
  resetDocumentsState, 
  resetSelectedSender, 
  resetSelectedReceiver, 
  resetSelectedAgent,
  setSelectedAgent,
  setExtractedData,
  setOcrInputID,
  addDocuments,
  setUploadedFiles,
  uploadComplete as uploadCompleteAction,
  migrateArrayToTabs,
  switchTab,
  updateTabEditableData,
  updateTabSelectedAgent,
  createTab,
  createWorkflowSession,
  switchWorkflowSession,
  updateWorkflowSessionState,
  updateWorkflowSessionName,
  setEditableData,
  addToProcessingQueue,
  removeFromProcessingQueue,
  setCurrentlyProcessingSession,
  clearProcessingQueue,
  fetchAdditionalContextPresets,
} from '../store/reducers';
import { 
  selectAllTabs, 
  selectActiveTabId, 
  selectActiveTabData,
  selectHasTabs,
} from '../store/selectors/tabSelectors';
import TabBar from '../components/Dashboard/TabBar';
import WorkflowTabBar from '../components/Dashboard/WorkflowTabBar';
import { FileCheck, FileJson, RotateCcw } from 'lucide-react';
import { MultiProcessingModalManager } from '../components/Dashboard/MultiProcessingModalManager';
import { ReviewUserDetails } from '../components/Dashboard/ReviewUserDetails';
import { ReviewReceiverDetails } from "../components/Dashboard/ReviewReceiverDetails";
import { ReviewSenderDetails } from "../components/Dashboard/ReviewSenderDetails";
import { ReviewAgents } from '../components/Dashboard/ReviewAgents';
import { FinalJsonData } from '../components/Dashboard/FinalJsonData';
import { ReviewExtractedData } from '../components/Dashboard/ReviewExtractedData';
import { GeneratedSuccessfullyModal } from '../components/Dashboard/GeneratedSuccessfullyModal';
import { DemoGeneratedSuccessfullyModal } from '../components/Dashboard/DemoGeneratedSuccessfullyModal';
import { RateLimitExceededModal } from '../components/Dashboard/RateLimitExceededModal';
import DUASpecification from '../components/Dashboard/DUASpecification';
import { scroller } from "react-scroll";
import { useDocumentProcessing } from '../hooks/useDocumentProcessing';
import { useDemoDocumentProcessing } from '../hooks/useDemoDocumentProcessing';
import { useLanguage, getTranslation } from '../hooks/useLanguage';
import { useCreditStats } from '../hooks/useApiHooks';

import star from '../assets/star.png'

import { useSenders, useAgents, useReceivers } from '../hooks/useApiHooks';
import { useJobStream } from '../hooks/useJobStream';
import { 
  restoreProcessedData, 
  restoreWorkflowData, 
  resetDashboard,
  restoreWorkflowFiles,
  clearWorkflowReduxState,
  syncReduxWithCache,
  checkFilesSync
} from '../utils/dashboardUtils';
import { setStoreInstance } from '../utils/processingQueueUtils';
import { processNextInQueue, handleJobIdSet, handleJobIdClear } from '../utils/dashboardQueueUtils';
import { saveTabToSession, loadTabFromSession, saveActiveTabId, loadActiveTabId, saveTabMetadata, loadTabMetadata } from '../utils/tabStorage';
import { getWorkflowFiles } from '../utils/workflowFileCache';
import { 
  saveWorkflowSessions, 
  loadWorkflowSessions, 
  saveActiveWorkflowSessionId, 
  loadActiveWorkflowSessionId,
  saveWorkflowLastJobId,
  loadWorkflowLastJobId,
  loadWorkflowOriginalExtractedData,
} from '../utils/workflowSessionStorage';
import { 
  normalizeWorkflowData,
  hasWorkflowData,
  getWorkflowDeclarations,
  shouldAutoDisplayData,
  restoreWorkflowSelections,
  shouldAutoRestoreWorkflow,
} from '../utils/workflowDataUtils';
import { useAllErrors } from '../hooks/useValidationErrors';
import { createEditableDataHash } from '../utils/hashUtils';
import { 
  isDemoMode, 
  dummyCompany, 
  getFinalAgents, 
  getFinalSenders, 
  getFinalReceivers,
  getScrollContainerId 
} from '../utils/demoUtils';

function Dashboard({ setBacktoDashboardClicked, backtoDashboardClicked, scrollToID, setIsStripeModalOpen }) {
  const navigate = useNavigate();
  
  // Company Information Form State
  const [companyFormData, setCompanyFormData] = useState({
    companyName: '',
    emailAddress: '',
    expectedDUAs: ''
  });

  // Unified logic: Load company data from session storage (priority) or URL params, redirect if missing
  useEffect(() => {
    // Check if we should skip redirect (e.g., after modal close)
    const skipRedirect = sessionStorage.getItem('skipRegisterRedirect');
    if (skipRedirect === 'true') {
      sessionStorage.removeItem('skipRegisterRedirect');
      // Don't redirect, set form data to empty so user can start fresh
      setCompanyFormData({
        companyName: '',
        emailAddress: '',
        expectedDUAs: ''
      });
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const urlEmail = urlParams.get('email');
    const urlCompany = urlParams.get('company');
    const urlDuas = urlParams.get('duas') || urlParams.get('expectedDuas');

    // Check session storage
    const savedEmail = sessionStorage.getItem('register_email');
    const savedCompany = sessionStorage.getItem('register_company');
    const savedExpectedDuas = sessionStorage.getItem('register_expectedDuas');

    // Priority: session storage > URL params (session storage is source of truth)
    const email = savedEmail || urlEmail || '';
    const company = savedCompany || urlCompany || '';
    const expectedDuas = savedExpectedDuas || urlDuas || '';

    // If no session storage AND no URL params, redirect to /register
    if (!savedEmail && !savedCompany && !savedExpectedDuas && !urlEmail && !urlCompany && !urlDuas) {
      navigate('/register');
      return;
    }

    // If session storage has data, remove URL params to prevent editing via URL
    if (savedEmail || savedCompany || savedExpectedDuas) {
      // Remove URL params if they exist
      if (urlEmail || urlCompany || urlDuas) {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    } else if (urlEmail || urlCompany || urlDuas) {
      // If we have URL params but no session storage, save URL params to session storage
      if (urlEmail) sessionStorage.setItem('register_email', urlEmail);
      if (urlCompany) sessionStorage.setItem('register_company', urlCompany);
      if (urlDuas) sessionStorage.setItem('register_expectedDuas', urlDuas);
      
      // Remove URL params after saving to session storage
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    // Set form data from merged sources
    setCompanyFormData({
      companyName: company ? decodeURIComponent(company) : '',
      emailAddress: email ? decodeURIComponent(email) : '',
      expectedDUAs: expectedDuas ? decodeURIComponent(expectedDuas) : ''
    });
  }, [navigate]);

  const handleCompanyFormChange = (e) => {
    const { name, value } = e.target;
    setCompanyFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Check if all company information fields are filled
  const isCompanyFormComplete = companyFormData.companyName.trim() !== '' && 
                                 companyFormData.emailAddress.trim() !== '' && 
                                 companyFormData.expectedDUAs.trim() !== '';

  // Load data from API hooks
  useSenders();
  useReceivers();
  useAgents();
  const { current, usage, exportCost, loading, error, refetch: refetchCreditStats } = useCreditStats();

  // Get active workflow session state (optimized to reduce redundant lookups)
  const activeWorkflowSessionState = useSelector((state) => {
    const workflowSessionsState = state.workflowSessions;
    const currentActiveSessionId = workflowSessionsState?.activeSessionId;
    if (currentActiveSessionId) {
      return workflowSessionsState?.sessions?.[currentActiveSessionId]?.state;
    }
    return null;
  });
  
  // Get global documents state once (for fallback)
  const globalDocumentsState = useSelector((state) => state.documents);
  
  // Workflow Sessions - for managing multiple workflow tabs (must be declared before useMemo hooks that use it)
  const workflowSessions = useSelector(state => state.workflowSessions);
  const activeWorkflowSessionId = workflowSessions?.activeSessionId;
  
  // CRITICAL: Use per-workflow File cache instead of global Redux state This ensures PDFs are isolated per workflow Force re-computation when activeWorkflowSessionId changes by including it in dependencies
  const documents = useMemo(() => {
    if (!activeWorkflowSessionId) return [];
    
    // CRITICAL: Only get files from per-workflow cache (source of truth) Do NOT fallback to Redux state - it may contain files from the previous workflow The restoration effect will sync Redux to match the cache Include Redux uploadComplete flag in dependencies to trigger recalculation when files are uploaded
    const cachedFiles = getWorkflowFiles(activeWorkflowSessionId);
    return cachedFiles || [];
  }, [activeWorkflowSessionId, globalDocumentsState?.uploadComplete, globalDocumentsState?.uploadedFiles?.length]);
  
  const uploadedFiles = useMemo(() => {
    if (!activeWorkflowSessionId) return [];
    
    // CRITICAL: Only get files from per-workflow cache (source of truth) Do NOT fallback to Redux state - it may contain files from the previous workflow The restoration effect will sync Redux to match the cache Include Redux uploadComplete flag in dependencies to trigger recalculation when files are uploaded
    const cachedFiles = getWorkflowFiles(activeWorkflowSessionId);
    return cachedFiles || [];
  }, [activeWorkflowSessionId, globalDocumentsState?.uploadComplete, globalDocumentsState?.uploadedFiles?.length]);
  
  // Use workflow session state for flags only (not File objects)
  const hasDocuments = useMemo(() => {
    return activeWorkflowSessionState?.hasDocuments ?? globalDocumentsState?.hasDocuments ?? false;
  }, [activeWorkflowSessionState, globalDocumentsState]);
  
  const uploadComplete = useMemo(() => {
    return activeWorkflowSessionState?.uploadComplete ?? globalDocumentsState?.uploadComplete ?? false;
  }, [activeWorkflowSessionState, globalDocumentsState]);
  
  const company = useSelector(state => state.company);
  const stepsCompleted = useSelector((state) => state.documents.stepsCompleted);
  
  const userData = useSelector((state) => state.user.userData);
  const receivers = useSelector((state) => state.receivers);
  const senders = useSelector((state) => state.senders);
  const agents = useSelector((state) => state.agents);
  
  // Check if we're in demo mode
  const isDemo = isDemoMode();
  
  // Use dummy data in demo mode if real data is not available
  const finalAgents = useMemo(() => getFinalAgents(agents, isDemo), [isDemo, agents]);
  const finalSenders = useMemo(() => getFinalSenders(senders, isDemo), [isDemo, senders]);
  const finalReceivers = useMemo(() => getFinalReceivers(receivers, isDemo), [isDemo, receivers]);
  
  // Memoize contacts array to avoid recreation on every render
  const contacts = useMemo(() => [
    ...finalSenders.map(sender => ({ ...sender, contactType: 'sender' })),
    ...finalReceivers.map(receiver => ({ ...receiver, contactType: 'receiver' }))
  ], [finalSenders, finalReceivers]);
  const activeWorkflowSession = activeWorkflowSessionId ? workflowSessions?.sessions?.[activeWorkflowSessionId] : null;
  const currentlyProcessingSessionId = workflowSessions?.currentlyProcessingSessionId;
  const processingQueue = workflowSessions?.processingQueue || [];

  const [isReviewConfirmed, setIsReviewConfirmed] = useState(false);
  const [isSuccessfulModalOpen, setIsSuccessfulModalOpen] = useState(false);
  const [specifyAmount, setSpecifyAmount] = useState([]);
  const [additionalContext, setAdditionalContext] = useState('');

  const dispatch = useDispatch();

  // Set dummy company data in Redux for demo mode if not already set
  useEffect(() => {
    if (isDemo && (!company || !company.nombre)) {
      dispatch({ type: 'SET_COMPANY', payload: dummyCompany });
    }
  }, [isDemo, company, dispatch]);

  const selectedSenders = useSelector(state => state.user.selectedSender || []);
  const selectedReceivers = useSelector(state => state.user.selectedReceiver || []);
  const selectedAgents = useSelector(state => state.user.selectedAgent || []);

  const extractedData = useSelector(state => state.documents.extractedData);

  // Get preparedJson from memoized session state - only update when taricJson actually changes
  const globalPreparedJson = useSelector((state) => state.preparedJson);
  const preparedJson = useMemo(() => {
    const sessionPreparedJson = activeWorkflowSessionState?.preparedJson;
    const jsonToUse = sessionPreparedJson ?? globalPreparedJson;
    return jsonToUse;
  }, [activeWorkflowSessionId, activeWorkflowSessionState?.preparedJson?.taricJson, globalPreparedJson?.taricJson]);
  
  // Tab system (for exports within a workflow)
  const tabs = useSelector(selectAllTabs);
  const activeTabId = useSelector(selectActiveTabId);
  const activeTabData = useSelector(selectActiveTabData);
  const hasTabs = useSelector(selectHasTabs);
  
  // Use active workflow session's editableData (all declarations together, tabs are just for UI viewing)
  const editableData = useSelector(state => {
    // Priority 1: Active workflow session's editableData (source of truth), normalize to array
    if (activeWorkflowSessionId && activeWorkflowSession?.state?.editableData) {
      const workflowEditableData = activeWorkflowSession.state.editableData;
      return Array.isArray(workflowEditableData) 
        ? workflowEditableData 
        : (workflowEditableData && typeof workflowEditableData === 'object' ? [workflowEditableData] : []);
    }
    // Priority 2: Fallback to global state (backward compatibility)
    return state.documents.editableData;
  });
  

  // CRITICAL: Use centralized hook for validation and TARIC JSON errors (per-workflow only)
  const {
    validationErrors,
    taricJsonErrors,
  } = useAllErrors();
  
  const ocrInputID = useSelector(state => state.documents.ocrInputID);

  // Use demo document processing hook in demo mode, otherwise use regular one
  const regularProcessing = useDocumentProcessing();
  const demoProcessing = useDemoDocumentProcessing();
  
  const {
    isGeneratingTaric,
    hasGenerationError,
    generationErrorMessage,
    processingCancelled,
    setProcessingCancelled,
    processDocuments: regularProcessDocuments,
    generateTaricDeclaration,
    rateLimitExceeded: demoRateLimitExceeded,
    setRateLimitExceeded: setDemoRateLimitExceeded,
    rateLimit: demoRateLimit
  } = isDemo ? demoProcessing : regularProcessing;
  
  const processDocuments = isDemo ? demoProcessing.processDocuments : regularProcessDocuments;
  const rateLimitExceeded = isDemo ? demoRateLimitExceeded : false;
  const setRateLimitExceeded = isDemo ? setDemoRateLimitExceeded : () => {};
  const rateLimit = isDemo ? demoRateLimit : null;
  
  // OPTIMIZED: Get isProcessing from memoized session state
  const isProcessing = useMemo(() => {
    return activeWorkflowSessionState?.isProcessing ?? false;
  }, [activeWorkflowSessionState]);
  
  // CRITICAL: Set isProcessing in workflow session state instead of global state
  const setIsProcessing = useCallback((value) => {
    if (activeWorkflowSessionId) {
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
        isProcessing: value,
      }));
    }
  }, [activeWorkflowSessionId, dispatch]);

  const [isSpanish] = useLanguage();
  
  // Keep stream open for suggestions_ready even after modal closes
  const [activeJobId, setActiveJobId] = useState(null);
  const { status: streamStatus } = useJobStream(activeJobId);

  // Track if this is the first mount to prevent clearing on every render
  const isFirstMountRef = useRef(true);
  const hasRestoredPdfsRef = useRef(false);
  const isRestoringSessionRef = useRef(false);
  
  // Track if stream is active to prevent workflow session interference
  const isStreamActiveRef = useRef(false);
  
  // Track if migration has happened to prevent infinite loops
  const hasMigratedRef = useRef(false);
  
  // Track last restored workflow session to prevent infinite restoration loops
  const lastRestoredWorkflowRef = useRef({ sessionId: null, dataHash: null });
  const activeWorkflowSessionIdRef = useRef(activeWorkflowSessionId);
  const workflowSwitchTimeoutRef = useRef(null);
  const extractedDataRef = useRef(extractedData);
  
  // Helper: Check if workflow is still active (prevents data inheritance)
  const isWorkflowStillActive = useCallback(() => {
    return activeWorkflowSessionIdRef.current === activeWorkflowSessionId;
  }, [activeWorkflowSessionId]);
  
  useEffect(() => {
    activeWorkflowSessionIdRef.current = activeWorkflowSessionId;
    extractedDataRef.current = extractedData;
  }, [activeWorkflowSessionId, extractedData]);
  
  // Update stream active status
  useEffect(() => {
    isStreamActiveRef.current = !!activeJobId;
  }, [activeJobId]);
  
  // Initialize workflow session on mount if none exists
  // Also fetch additional context presets on page load
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      
      // Fetch additional context presets on page load
      dispatch(fetchAdditionalContextPresets());
      
      // Try to load from sessionStorage first
      const savedSessions = loadWorkflowSessions();
      if (savedSessions && savedSessions.sessionOrder.length > 0) {
        // Restore ALL sessions in the order they were saved
        savedSessions.sessionOrder.forEach((sessionId) => {
          const session = savedSessions.sessions[sessionId];
          if (session) {
            dispatch(createWorkflowSession(sessionId, session.name, session.state));
          }
        });
        
        // Switch to the active session (or first one if active doesn't exist)
        const sessionToActivate = savedSessions.activeSessionId || savedSessions.sessionOrder[0];
        if (savedSessions.sessions[sessionToActivate]) {
          dispatch(switchWorkflowSession(sessionToActivate));
        } else if (savedSessions.sessionOrder.length > 0) {
          dispatch(switchWorkflowSession(savedSessions.sessionOrder[0]));
        }
      } else if (!activeWorkflowSessionId && (!workflowSessions || workflowSessions.sessionOrder?.length === 0)) {
        // Create initial session if none exists
        dispatch(createWorkflowSession());
      }
    }
  }, []);
  
  // Get store reference for use in effects (must be declared before useEffects that use it)
  const store = useStore();
  
  // DEBUG: Log workflow state when validation errors exist but no editableData OPTIMIZED: Only run when validationErrors actually changes, not when other data changes
  const prevValidationErrorsRef = useRef(null);
  useEffect(() => {
    // Only run if validationErrors actually changed
    const errorsChanged = JSON.stringify(prevValidationErrorsRef.current) !== JSON.stringify(validationErrors);
    if (!errorsChanged && validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
      return; // Skip if errors haven't changed
    }
    
    prevValidationErrorsRef.current = validationErrors;
    
    if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
      const currentState = store.getState();
      const currentWorkflowSession = currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const workflowState = currentWorkflowSession?.state;
      
      // Get editableData and extractedData from store inside effect (not as dependencies)
      const currentEditableData = currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId]?.state?.editableData || 
                                   currentState?.documents?.editableData;
      const currentExtractedData = currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId]?.state?.extractedData || 
                                   currentState?.documents?.extractedData;
      
      const hasEditableData = workflowState?.editableData && 
        (Array.isArray(workflowState.editableData) ? workflowState.editableData.length > 0 : Object.keys(workflowState.editableData || {}).length > 0);
      const hasExtractedData = workflowState?.extractedData && 
        (Array.isArray(workflowState.extractedData) ? workflowState.extractedData.length > 0 : Object.keys(workflowState.extractedData || {}).length > 0);
      const hasReduxEditableData = currentEditableData && 
        (Array.isArray(currentEditableData) ? currentEditableData.length > 0 : Object.keys(currentEditableData || {}).length > 0);
      const hasReduxExtractedData = currentExtractedData && 
        (Array.isArray(currentExtractedData) ? currentExtractedData.length > 0 : Object.keys(currentExtractedData || {}).length > 0);
      
    }
  }, [validationErrors, activeWorkflowSessionId, store]); // Removed editableData, extractedData, stepsCompleted to prevent unnecessary re-renders
  
  // Save workflow sessions to sessionStorage when they change
  useEffect(() => {
    if (workflowSessions && workflowSessions.sessions && workflowSessions.sessionOrder) {
      saveWorkflowSessions(
        workflowSessions.sessions,
        workflowSessions.sessionOrder,
        workflowSessions.activeSessionId
      );
      if (workflowSessions.activeSessionId) {
        saveActiveWorkflowSessionId(workflowSessions.activeSessionId);
      }
    }
  }, [workflowSessions]);
  
  // CRITICAL: Save all workflows immediately before page unload/refresh This ensures all workflows are saved even if debounced save hasn't completed
  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentState = store.getState();
      const currentWorkflowSessions = currentState?.workflowSessions;
      if (currentWorkflowSessions && currentWorkflowSessions.sessions && currentWorkflowSessions.sessionOrder) {
        // Use immediate save to ensure data is persisted before page unloads This bypasses debouncing and writes directly to sessionStorage
        saveWorkflowSessions(
          currentWorkflowSessions.sessions,
          currentWorkflowSessions.sessionOrder,
          currentWorkflowSessions.activeSessionId,
          true // immediate = true to bypass debouncing
        );
        if (currentWorkflowSessions.activeSessionId) {
          saveActiveWorkflowSessionId(currentWorkflowSessions.activeSessionId);
        }
      }
    };
    
    // Use both beforeunload and visibilitychange for better coverage
    window.addEventListener('beforeunload', handleBeforeUnload);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleBeforeUnload();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [store]);
  
  // Set store instance for queue utils (centralized queue management)
  useEffect(() => {
    setStoreInstance(store);
  }, [store]);
  
  // Save current state to active workflow session (debounced, but NOT during active streaming) CRITICAL: editableData is stored in workflow session state (all declarations together, NOT split by tabs) OPTIMIZED: Removed editableData from dependencies - it's handled by separate useEffect to prevent lag
  const saveWorkflowStateTimerRef = useRef(null);
  
  // Track last saved state to prevent unnecessary updates
  const lastSavedStateRef = useRef(null);
  
  useEffect(() => {
    // Clear existing timer
    if (saveWorkflowStateTimerRef.current) {
      clearTimeout(saveWorkflowStateTimerRef.current);
    }
    
    // CRITICAL: Don't save workflow session state during active streaming This prevents overwriting stream data with stale state
    if (activeWorkflowSessionId && !isRestoringSessionRef.current && !isStreamActiveRef.current) {
      // Debounce the save to avoid saving on every render (increased to 2000ms to reduce lag)
      saveWorkflowStateTimerRef.current = setTimeout(() => {
        // Get current workflow session state to preserve existing data (selectedSender, selectedReceiver, selectedAgent)
        const currentState = store.getState();
        const currentWorkflowSession = currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId];
        if (!currentWorkflowSession) return; // Guard: workflow might have been deleted
        
        const currentWorkflowState = currentWorkflowSession?.state;
        const currentEditableData = currentWorkflowState?.editableData;
        const currentExtractedData = currentWorkflowState?.extractedData;
        
        // CRITICAL: Only use workflow session selections - DO NOT fallback to global Redux This prevents copying stale selections from previous workflows to new workflows Workflow session state is the source of truth - if it's empty, selections should be empty
        const currentSelectedSender = Array.isArray(currentWorkflowState?.selectedSender) 
          ? currentWorkflowState.selectedSender 
          : [];
        const currentSelectedReceiver = Array.isArray(currentWorkflowState?.selectedReceiver) 
          ? currentWorkflowState.selectedReceiver 
          : [];
        const currentSelectedAgent = Array.isArray(currentWorkflowState?.selectedAgent) 
          ? currentWorkflowState.selectedAgent 
          : [];
        const currentSelectedTemplate = currentWorkflowState?.selectedTemplate || null;
        
        // OPTIMIZED: Get data sources - prefer Redux if processing, otherwise use workflow session
        const isProcessing = currentState?.workflowSessions?.currentlyProcessingSessionId === activeWorkflowSessionId || 
                             currentWorkflowState?.isProcessing === true;
        const reduxExtractedData = store.getState().documents?.extractedData;
        const reduxPreparedJson = store.getState().preparedJson;
        
        const extractedDataToSave = (isProcessing || stepsCompleted) && reduxExtractedData 
          ? reduxExtractedData 
          : (currentExtractedData || null);
        
        const editableDataToSave = stepsCompleted && extractedDataToSave && 
          (!currentEditableData || normalizeWorkflowData(currentEditableData).length === 0)
          ? extractedDataToSave 
          : (currentEditableData || null);
        
        const preparedJsonToUse = reduxPreparedJson || preparedJson;
        
        // OPTIMIZED: Simple change detection - only check key fields
        const stateSignature = `${activeWorkflowSessionId}_${stepsCompleted}_${documents?.length || 0}_${isProcessing}`;
        if (lastSavedStateRef.current === stateSignature) return;
        lastSavedStateRef.current = stateSignature;
        
        const workflowStateUpdate = {
          documents,
          hasDocuments,
          uploadComplete,
          uploadedFiles,
          stepsCompleted,
          // CRITICAL: Use workflow session's selectedSender/Receiver/Agent (preserve existing selections) DO NOT use global selectedSenders/selectedReceivers/selectedAgents - they are empty arrays
          selectedSender: currentSelectedSender,
          selectedReceiver: currentSelectedReceiver,
          selectedAgent: currentSelectedAgent,
          selectedTemplate: currentSelectedTemplate, // CRITICAL: Preserve per-workflow template
          validationErrors,
          preparedJson: preparedJsonToUse, // Use latest preparedJson from global state
          taricJsonErrors,
          ocrInputID,
          specifyAmount,
          additionalContext,
          // CRITICAL: Save editableData - use from extractedData if workflow just completed and editableData is empty
          editableData: editableDataToSave,
          extractedData: extractedDataToSave, // CRITICAL: Save extractedData from Redux if processing/completing
        };
        
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, workflowStateUpdate));
      }, 2000); // Increased debounce to 2s to reduce lag during typing
    }
    
    return () => {
      if (saveWorkflowStateTimerRef.current) {
        clearTimeout(saveWorkflowStateTimerRef.current);
      }
    };
  }, [
    activeWorkflowSessionId,
    documents,
    hasDocuments,
    uploadComplete,
    uploadedFiles,
    stepsCompleted,
    // NOTE: selectedSenders/selectedReceivers/selectedAgents removed - we preserve from workflow session state
    validationErrors,
    preparedJson,
    taricJsonErrors,
    ocrInputID,
    specifyAmount,
    additionalContext,
    // REMOVED: extractedData from dependencies - it's checked inside the timeout callback This prevents the loop where extractedData changes trigger the effect, which saves state, which changes extractedData OPTIMIZED: Removed editableData from dependencies to prevent lag It's handled by separate useEffect above
    dispatch,
    store,
    activeJobId, // Include activeJobId to prevent saves during streaming
  ]);
  
  // Track previous session ID to detect actual session switches
  const prevSessionIdRef = useRef(null);
  
  // CRITICAL: Initialize prevSessionIdRef on mount if activeWorkflowSessionId exists This ensures proper switch detection when workflows already exist
  useEffect(() => {
    if (activeWorkflowSessionId && prevSessionIdRef.current === null) {
      prevSessionIdRef.current = activeWorkflowSessionId;
    }
  }, [activeWorkflowSessionId]);
  
  // CRITICAL: Save extractedData and editableData when workflow completes This ensures data is saved even if the debounced save effect doesn't catch it
  const prevStepsCompletedRef = useRef(stepsCompleted);
  useEffect(() => {
    // Check if workflow just completed (stepsCompleted changed from false to true)
    const justCompleted = !prevStepsCompletedRef.current && stepsCompleted;
    prevStepsCompletedRef.current = stepsCompleted;
    
    if (justCompleted && activeWorkflowSessionId && !isRestoringSessionRef.current && !isStreamActiveRef.current) {
      const currentState = store.getState();
      const currentReduxExtractedData = currentState?.documents?.extractedData;
      const currentWorkflowSession = currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const currentWorkflowState = currentWorkflowSession?.state;
      const currentEditableData = currentWorkflowState?.editableData;
      const currentExtractedData = currentWorkflowState?.extractedData;
      
      // Only save if we have extractedData in Redux and workflow doesn't have it yet
      if (currentReduxExtractedData && normalizeWorkflowData(currentReduxExtractedData).length > 0) {
        const hasWorkflowData = currentExtractedData && normalizeWorkflowData(currentExtractedData).length > 0;
        
        // Save extractedData and editableData if workflow doesn't have it
        if (!hasWorkflowData) {
          
          // Populate editableData from extractedData if it's empty
          const editableDataToSave = (currentEditableData && normalizeWorkflowData(currentEditableData).length > 0)
            ? currentEditableData
            : currentReduxExtractedData;
          
          dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
            extractedData: currentReduxExtractedData,
            editableData: editableDataToSave,
            stepsCompleted: true,
          }));
          
          // Clear the state signature to allow the debounced save to run if needed
          lastSavedStateRef.current = null;
        }
      }
    }
  }, [stepsCompleted, activeWorkflowSessionId, dispatch, store, isRestoringSessionRef]);
  
  // CRITICAL: Auto-set stepsCompleted when workflow has data (even on initial mount) This ensures data displays even if stepsCompleted was false in sessionStorage OPTIMIZED: Memoize workflow data checks to avoid redundant computations CRITICAL: Use sessionId in dependency to prevent unnecessary recalculations
  const workflowDataInfo = useMemo(() => {
    if (!activeWorkflowSession?.state) return null;
    const sessionState = activeWorkflowSession.state;
    const normalizedExtracted = normalizeWorkflowData(sessionState.extractedData);
    const normalizedEditable = normalizeWorkflowData(sessionState.editableData);
    return {
      sessionState,
      hasWorkflowData: hasWorkflowData(sessionState),
      shouldDisplay: shouldAutoDisplayData(sessionState),
      declarations: getWorkflowDeclarations(sessionState),
      hasExtractedData: normalizedExtracted.length > 0,
      hasEditableData: normalizedEditable.length > 0,
    };
  }, [activeWorkflowSession, activeWorkflowSessionId]); // Add sessionId to stabilize memo
  
  // OPTIMIZED: Unified workflow restoration - prevents data inheritance between workflows
  useEffect(() => {
    // Skip if conditions not met, BUT check if we're switching to a different workflow If we're switching workflows, we need to process it even if flag is set
    const isSwitchingWorkflow = prevSessionIdRef.current !== null && 
                                 prevSessionIdRef.current !== activeWorkflowSessionId;
    
    if (isRestoringSessionRef.current && !isSwitchingWorkflow) {
      return;
    }
    
    // If we're switching workflows but flag is set, reset it first
    if (isRestoringSessionRef.current && isSwitchingWorkflow) {
      isRestoringSessionRef.current = false;
    }
    
    if (!activeWorkflowSession) {
      return;
    }
    
    // CRITICAL: Check if CURRENT active workflow is processing, not any workflow
    // Get the jobId for the active workflow specifically
    const activeWorkflowJobId = loadWorkflowLastJobId(activeWorkflowSessionId);
    const isActiveWorkflowStreaming = activeJobId && activeJobId === activeWorkflowJobId;
    
    // Only block restoration if the ACTIVE workflow is streaming
    // If we're switching workflows, allow restoration even if another workflow is processing
    if (isActiveWorkflowStreaming && !isSwitchingWorkflow) {
      return;
    }
    
    // CRITICAL: Detect workflow switch - either explicit switch OR first time setting prevSessionIdRef This handles both: switching between existing workflows AND creating a new workflow
    const isSessionSwitch = prevSessionIdRef.current !== null && prevSessionIdRef.current !== activeWorkflowSessionId;
    const isFirstWorkflowActivation = prevSessionIdRef.current === null && activeWorkflowSessionId !== null;
    
    // CRITICAL: Always clear Redux data FIRST when switching workflows to prevent inheritance This includes both explicit switches and when a new workflow is created
    if (isSessionSwitch || isFirstWorkflowActivation) {
      // CRITICAL: Update activeJobId IMMEDIATELY for the new workflow
      // This must happen before any restoration logic to prevent blocking
      const newWorkflowJobId = loadWorkflowLastJobId(activeWorkflowSessionId);
      if (newWorkflowJobId) {
        setActiveJobId(newWorkflowJobId);
        lastProcessedJobIdRef.current = newWorkflowJobId;
      } else {
        setActiveJobId(null);
        lastProcessedJobIdRef.current = null;
        // Clear global activeJobId if current workflow doesn't have one
        if (sessionStorage.getItem('activeJobId')) {
          sessionStorage.removeItem('activeJobId');
        }
      }
      
      // CRITICAL: Set ALL refs IMMEDIATELY to prevent infinite loop (before any dispatches)
      prevSessionIdRef.current = activeWorkflowSessionId;
      lastRestoredWorkflowRef.current = { sessionId: null, dataHash: null };
      activeWorkflowSessionIdRef.current = activeWorkflowSessionId;
      isRestoringSessionRef.current = true; // Set BEFORE dispatches to prevent re-triggering
      
      // CRITICAL: Reset TARIC JSON generation refs when switching workflows This ensures new workflow can regenerate JSON properly
      lastSavedEditableDataHashRef.current = null;
      lastTaricGenerationRef.current = null;
      
      // CRITICAL: Reset save effect refs when switching workflows to prevent debounced save effect from overwriting saved editableData
      lastStoredWorkflowSessionIdRef.current = null;
      lastStoredEditableDataRef.current = null;
      
      // OPTIMIZED: Use utility function to clear Redux state (batches dispatches) This also clears selections from global Redux state
      clearWorkflowReduxState({
        workflowSessionId: activeWorkflowSessionId,
        dispatch,
        setExtractedData,
        setEditableData,
        setStepsCompleted,
        updateWorkflowSessionState
      });
      
      // CRITICAL: Always restore selections (even if workflow has no state or empty selections) This ensures global Redux is cleared when workflow has no selections, preventing inheritance
      restoreWorkflowSelections(activeWorkflowSession?.state || null, dispatch);
      
      // OPTIMIZED: Use utility function to restore files (batches dispatches)
      restoreWorkflowFiles({
        workflowSessionId: activeWorkflowSessionId,
        dispatch,
        store: process.env.NODE_ENV === 'development' ? store : null
      });
      
      // CRITICAL: Reset restoration flag after a delay to allow Redux updates to propagate This MUST happen even if we exit early, so schedule it immediately
      setTimeout(() => {
        isRestoringSessionRef.current = false;
      }, 200);
      
      // Early exit if workflow has no state at all
      if (!activeWorkflowSession.state) {
        dispatch(resetDocumentsState());
        dispatch({ type: 'RESET_TABS' });
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { isProcessing: false }));
        if (!loadWorkflowLastJobId(activeWorkflowSessionId)) {
          sessionStorage.removeItem('activeJobId');
          setActiveJobId(null);
        }
        // Flag reset already scheduled above
        return;
      }
      
      // Early exit if workflow has no lastJobId - means it definitely has no processed data yet
      const workflowJobId = loadWorkflowLastJobId(activeWorkflowSessionId);
      // OPTIMIZED: Compute workflowDataInfo inline to avoid dependency issues
      const sessionState = activeWorkflowSession.state;
      const hasWorkflowDataValue = hasWorkflowData(sessionState);
      if (!workflowJobId && !hasWorkflowDataValue) {
        // Flag reset already scheduled above
        return;
      }
    }
    
    // Verify workflow still active before proceeding
    if (activeWorkflowSessionIdRef.current !== activeWorkflowSessionId) {
      return;
    }
    
    // OPTIMIZED: Compute workflowDataInfo inline (not from memo) to avoid dependency loop
    if (!activeWorkflowSession?.state) {
      return;
    }
    
    const sessionState = activeWorkflowSession.state;
    const declarations = getWorkflowDeclarations(sessionState);
    const hasActualData = hasWorkflowData(sessionState) && declarations?.length > 0;
    const workflowJobId = loadWorkflowLastJobId(activeWorkflowSessionId);
    
    // CRITICAL: Restore ocrInputID even if there's no declarations data
    // This ensures ocrInputID persists across page refreshes even when data hasn't been processed yet
    if (sessionState.ocrInputID) {
      dispatch(setOcrInputID(sessionState.ocrInputID));
    }
    
    // Early exit if no data to restore
    if ((!workflowJobId && !hasActualData) || !hasActualData || !declarations?.length) {
      return;
    }
    
    // CRITICAL: Only skip restoration if same workflow AND same hash - when switching workflows, always restore to ensure saved data is loaded
    const declarationsHash = `${declarations.length}_${declarations.map(d => d?.id || d?.declarationId || '').filter(Boolean).join('_')}`;
    const isSameWorkflow = lastRestoredWorkflowRef.current.sessionId === activeWorkflowSessionId;
    if (isSameWorkflow && lastRestoredWorkflowRef.current.dataHash === declarationsHash) {
      return;
    }
    
    // Perform restoration - set flag if not already set (for non-switch cases)
    if (!isRestoringSessionRef.current) {
      isRestoringSessionRef.current = true;
    }
    
    // Update tracking ref BEFORE dispatch
    lastRestoredWorkflowRef.current = { sessionId: activeWorkflowSessionId, dataHash: declarationsHash };
    
    // PDFs are now stored only in Redux state (localStorage disabled to avoid quota issues) File metadata is still stored in workflow session state for reference, but actual File objects are only in Redux and will be lost on page refresh Note: This is intentional to avoid localStorage quota issues
    if (sessionState.uploadComplete) {
      dispatch(uploadCompleteAction());
    }
    
    // OPTIMIZED: Normalize and restore declarations (already validated above)
    const normalizedDeclarations = Array.isArray(declarations) ? declarations : [declarations];
    
    dispatch(setExtractedData(normalizedDeclarations));
    dispatch(setEditableData(normalizedDeclarations));
    dispatch(setStepsCompleted(true));
    
    // CRITICAL: Update tabs to match restored editableData to prevent tabs with old data from overwriting restored data
    const stateForTabs = store.getState();
    const currentTabs = stateForTabs?.tabs?.tabs || {};
    const tabOrder = stateForTabs?.tabs?.tabOrder || [];
    
    if (tabOrder.length > 0 && normalizedDeclarations.length > 0) {
      normalizedDeclarations.forEach((declaration, index) => {
        const tabId = tabOrder[index];
        if (tabId && currentTabs[tabId]) {
          dispatch(updateTabEditableData(tabId, declaration));
        }
      });
    }
    
    // CRITICAL: Update save effect refs to match restored data so debounced save effect won't overwrite saved editableData
    const restoredDataHash = createEditableDataHash(normalizedDeclarations);
    lastStoredEditableDataRef.current = restoredDataHash;
    lastStoredWorkflowSessionIdRef.current = activeWorkflowSessionId;
    
    // Restore additional state (ocrInputID already restored above, but restore other state here)
    restoreWorkflowSelections(sessionState, dispatch);
    if (sessionState.specifyAmount) setSpecifyAmount(sessionState.specifyAmount);
    if (sessionState.additionalContext !== undefined) setAdditionalContext(sessionState.additionalContext);
    
    // CRITICAL: Validate immediately after restoring data
    const { validateRequiredFieldsOnly } = require('../utils/validationUtils');
    const currentState = store.getState();
    const isSpanish = currentState?.validationErrors?.isSpanish ?? true;
    const errors = validateRequiredFieldsOnly(normalizedDeclarations, isSpanish);
    
    // Store validation errors immediately (don't overwrite editableData)
    dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
      validationErrors: errors,
    }));
    
    // CRITICAL: Trigger validation after data is restored to recalculate errors This ensures validation errors match the current data, not stale session storage errors
    setTimeout(() => {
      // Validation will be triggered automatically by useDeclarationSummary when editableData changes
      isRestoringSessionRef.current = false;
    }, 300); // Increased delay to allow validation to run
  }, [activeWorkflowSessionId, activeWorkflowSession, dispatch]);
  
  // CRITICAL: Sync Redux state with per-workflow cache when workflow changes This ensures UploadDocuments component (which reads from Redux) shows correct files Skip during restoration to avoid race conditions - restoration logic handles Redux updates
  useEffect(() => {
    if (!activeWorkflowSessionId) {
      return;
    }
    
    // CRITICAL: Skip sync if we're currently restoring (restoration logic handles Redux updates) Also skip if we just switched workflows (restoration will handle it)
    if (isRestoringSessionRef.current) {
      return;
    }
    
    // Check if this is a workflow switch - if so, wait longer for restoration to complete
    const isSwitchingWorkflow = prevSessionIdRef.current !== null && 
                                 prevSessionIdRef.current !== activeWorkflowSessionId;
    const delay = isSwitchingWorkflow ? 300 : 50; // Longer delay after switch
    
    // Add a delay to ensure Redux state has updated after any dispatches This prevents seeing stale Redux state during workflow switches
    const timeoutId = setTimeout(() => {
      // Double-check we're not restoring anymore
      if (isRestoringSessionRef.current) {
        return;
      }
      
      // OPTIMIZED: Use utility function to sync Redux with cache (only updates if out of sync)
      syncReduxWithCache({
        workflowSessionId: activeWorkflowSessionId,
        reduxFiles: globalDocumentsState?.uploadedFiles,
        dispatch
      });
    }, delay);
    
    return () => clearTimeout(timeoutId);
  }, [activeWorkflowSessionId, dispatch, globalDocumentsState?.uploadedFiles]);
  
  useEffect(() => {
    dispatch(setShowBackToDashboardButton(false));
    
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
    }
    
    // Fix stuck state: If extractedData exists but stepsCompleted is false and no way to restore
    const hasExtractedData = normalizeWorkflowData(extractedData).length > 0;
    const hasTabData = tabs && tabs.length > 0 && tabs.some(tab => tab.editableData);
    const hasActiveJob = sessionStorage.getItem('activeJobId');
    const hasCompletedJob = sessionStorage.getItem('hasCompletedJob');
    const lastJobId = activeWorkflowSessionId ? loadWorkflowLastJobId(activeWorkflowSessionId) : null;
    const hasWorkflowDataValue = activeWorkflowSession?.state && hasWorkflowData(activeWorkflowSession.state);
    
    // Only clear if there's no way to restore (no active job, no completed job, no lastJobId, no tab data, no workflow data)
    if (hasExtractedData && !stepsCompleted && !hasActiveJob && !hasCompletedJob && !lastJobId && !hasTabData && !hasWorkflowDataValue) {
      console.warn('Dashboard: extractedData exists but stepsCompleted is false - clearing extractedData to prevent stuck state');
      dispatch(setExtractedData(null));
    }
  }, [dispatch, extractedData, stepsCompleted, tabs, activeWorkflowSessionId, activeWorkflowSession]);
  
  // Track the last job ID we've processed to avoid duplicate renames
  const lastProcessedJobIdRef = useRef(null);
  
  // Listen for clear processing state event when creating new workflow CRITICAL: Don't include isProcessing in dependencies to prevent loops
  useEffect(() => {
    const handleClearProcessing = () => {
      if (activeWorkflowSessionId) {
        setIsProcessing(false);
      }
    };
    
    window.addEventListener('clearProcessing', handleClearProcessing);
    
    return () => {
      window.removeEventListener('clearProcessing', handleClearProcessing);
    };
  }, [setIsProcessing, activeWorkflowSessionId]); // Removed isProcessing to prevent loop
  
  // Track if we're currently processing the next workflow to prevent loops
  const isProcessingNextInQueueRef = useRef(false);
  
  // Listen for processNextInQueue event - when a workflow finishes, process the next one
  useEffect(() => {
    const handleProcessNextInQueueEvent = async (event) => {
      const completedSessionId = event.detail?.completedSessionId;
      
      const currentState = store.getState();
      const workflowSessionsState = currentState?.workflowSessions;
      const queue = workflowSessionsState?.processingQueue || [];
      const allSessions = workflowSessionsState?.sessions || {};
      let currentlyProcessingId = workflowSessionsState?.currentlyProcessingSessionId;
      
      // CRITICAL: Proactively clear currentlyProcessingId if it points to ANY completed workflow This handles cases where the event wasn't dispatched or state is stale
      if (currentlyProcessingId) {
        const currentlyProcessingSession = allSessions[currentlyProcessingId];
        if (currentlyProcessingSession?.state?.stepsCompleted) {
          dispatch(setCurrentlyProcessingSession(null));
          currentlyProcessingId = null; // Update local variable
        }
      }
      
      if (completedSessionId) {
        // CRITICAL: ALWAYS clear currentlyProcessingSessionId if it matches the completed workflow OR if it points to any completed workflow (safety check) Do this FIRST before any other state updates to ensure it's cleared immediately
        const completedSession = allSessions[completedSessionId];
        const isCompleted = completedSession?.state?.stepsCompleted || false;
        
        if (currentlyProcessingId === completedSessionId || 
            (currentlyProcessingId && allSessions[currentlyProcessingId]?.state?.stepsCompleted)) {
          dispatch(setCurrentlyProcessingSession(null));
        }
        
        // Also ensure isProcessing is false for the completed workflow
        dispatch(updateWorkflowSessionState(completedSessionId, {
          isProcessing: false,
        }));
      }
      
      const result = await processNextInQueue({
        completedSessionId,
        store,
        dispatch,
        isProcessingNextInQueueRef,
      });
      
      // Process next workflow in queue
    };
    
    window.addEventListener('processNextInQueue', handleProcessNextInQueueEvent);
    
    return () => {
      window.removeEventListener('processNextInQueue', handleProcessNextInQueueEvent);
    };
  }, [dispatch, store]);
  
  // REMOVED: Auto-processing effect - workflows process immediately when user clicks button Since backend supports multiple concurrent jobs, workflows don't need to be queued They process immediately when the "Process Docs" button is clicked
  
  // Listen for jobId from ProcessingModal and keep stream open Also rename workflow session to job ID when processing starts (only once per job ID)
  useEffect(() => {
    const handleJobIdSetEvent = (event) => {
      if (!event.detail?.jobId) {
        return;
      }
      
      const jobId = event.detail.jobId;
      const eventWorkflowSessionId = event.detail?.workflowSessionId;
      
      // Use utility function to handle job ID set
      handleJobIdSet({
        jobId,
        eventWorkflowSessionId,
        store,
        setActiveJobId,
        dispatch,
        lastProcessedJobIdRef,
      });
    };
    
    const handleJobIdClearEvent = () => {
      // Use utility function to handle job ID clear
      handleJobIdClear({
        setActiveJobId,
        lastProcessedJobIdRef,
      });
    };
    
    window.addEventListener('setActiveJobId', handleJobIdSetEvent);
    window.addEventListener('clearActiveJobId', handleJobIdClearEvent);
    
    return () => {
      window.removeEventListener('setActiveJobId', handleJobIdSetEvent);
      window.removeEventListener('clearActiveJobId', handleJobIdClearEvent);
    };
  }, [activeWorkflowSessionId, dispatch, workflowSessions]);
  
  // Track previous workflow session ID to detect changes
  const prevActiveWorkflowSessionIdForJobIdRef = useRef(null);
  
  // Check for active jobId in sessionStorage when workflow changes CRITICAL: Only restore jobId if it belongs to the active workflow
  useEffect(() => {
    if (!activeWorkflowSessionId) {
      prevActiveWorkflowSessionIdForJobIdRef.current = null;
      setActiveJobId(null);
      lastProcessedJobIdRef.current = null;
      return;
    }
    
    // Only process if workflow actually changed (not on initial mount if same workflow)
    const workflowChanged = prevActiveWorkflowSessionIdForJobIdRef.current !== activeWorkflowSessionId;
    prevActiveWorkflowSessionIdForJobIdRef.current = activeWorkflowSessionId;
    
    // Get jobId from active workflow's sessionStorage (not global)
    const workflowJobId = loadWorkflowLastJobId(activeWorkflowSessionId);
    const globalActiveJobId = sessionStorage.getItem('activeJobId');
    
    if (workflowJobId) {
      // Workflow has a job ID - set it only if workflow changed
      if (workflowChanged) {
        setActiveJobId(workflowJobId);
        lastProcessedJobIdRef.current = workflowJobId;
      }
    } else {
      // Workflow doesn't have a job ID - clear activeJobId This prevents inheriting jobId from previous workflow
      setActiveJobId(null);
      lastProcessedJobIdRef.current = null;
      // Clear global activeJobId if current workflow doesn't have one
      if (globalActiveJobId) {
        sessionStorage.removeItem('activeJobId');
      }
    }
  }, [activeWorkflowSessionId]);

  // Check if restore button should be shown (per-tab basis)
  const [showRestoreButton, setShowRestoreButton] = useState(false);

  useEffect(() => {
    // Check if restore button should be shown on page refresh Show restore button if: 1. No data is currently displayed (extractedData is empty/null AND stepsCompleted is false) 2. BUT data exists in sessionStorage (tabs OR job data) 3. AND processing is NOT happening for this active workflow
    
    // Check if processing is happening for the active workflow
    const lastJobId = activeWorkflowSessionId ? loadWorkflowLastJobId(activeWorkflowSessionId) : null;
    const isActiveWorkflowProcessing = isProcessing && activeJobId && lastJobId && activeJobId === lastJobId;
    
    // If processing is happening for the active workflow, don't show restore button
    if (isActiveWorkflowProcessing) {
      setShowRestoreButton(false);
      return;
    }
    
    // Check if data is currently displayed
    const hasDisplayedData = normalizeWorkflowData(extractedData).length > 0;
    const isDataVisible = hasDisplayedData && stepsCompleted;
    
    // If data is already visible, don't show restore button
    if (isDataVisible) {
      setShowRestoreButton(false);
      return;
    }
    
    // Check if data exists in sessionStorage but hasn't been restored yet
    const storedMetadata = loadTabMetadata();
    const hasStoredTabs = storedMetadata && storedMetadata.length > 0;
    
    // Check if any tab has stored data in sessionStorage
    const hasStoredTabData = (() => {
      if (hasStoredTabs) {
        try {
          // Check if any tab has stored editableData
          return storedMetadata.some(tabMeta => {
            const tabEditableDataKey = `tab-${tabMeta.tabId}-editableData`;
            const stored = sessionStorage.getItem(tabEditableDataKey);
            return !!stored;
          });
        } catch (e) {
          return false;
        }
      }
      return false;
    })();
    
    // Show restore button if: data is not visible BUT (has stored tabs OR has jobId)
    const shouldShowRestore = !isDataVisible && (hasStoredTabs || hasStoredTabData || lastJobId);
    setShowRestoreButton(shouldShowRestore);
  }, [extractedData, stepsCompleted, activeWorkflowSessionId, isProcessing, activeJobId]);

  // Function to restore processed data from sessionStorage
  const handleRestoreData = async () => {
    try {
      // First, restore tabs from sessionStorage
      const storedMetadata = loadTabMetadata();
      const storedActiveTabId = loadActiveTabId();
      
      if (storedMetadata && storedMetadata.length > 0 && !hasLoadedTabsFromStorageRef.current) {
        // Restore tabs from sessionStorage
        storedMetadata.forEach((tabMeta) => {
          // Check if tab already exists before creating
          const existingTab = tabs.find(t => t.tabId === tabMeta.tabId);
          if (!existingTab) {
            const tabData = loadTabFromSession(tabMeta.tabId);
            
            // Create tab with stored data
            dispatch(createTab(tabMeta.tabId, tabMeta.declarationIndex, {
              ...(tabData || {}),
              label: tabMeta.label,
              status: tabMeta.status || 'draft',
            }));
          }
        });
        
        // Restore active tab
        if (storedActiveTabId) {
          dispatch(switchTab(storedActiveTabId));
        }
        
        // Mark as loaded
        hasLoadedTabsFromStorageRef.current = true;
        hasManuallyRestoredRef.current = true;
      }
      
      // Wait a bit for tabs to be created in Redux
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Use a callback approach - get tabs from sessionStorage directly since we just loaded them
      const allTabEditableData = [];
      if (storedMetadata && storedMetadata.length > 0) {
        storedMetadata.forEach((tabMeta) => {
          const tabData = loadTabFromSession(tabMeta.tabId);
          if (tabData && tabData.editableData) {
            allTabEditableData.push(tabData.editableData);
          }
        });
      }
      
      // Restore data from tabs if available
      if (allTabEditableData.length > 0) {
        // Normalize to array format
        const normalizedData = allTabEditableData.flat();
        dispatch(setExtractedData(normalizedData));
        
        // Get active tab data
        if (storedActiveTabId) {
          const activeTabData = storedMetadata.find(m => m.tabId === storedActiveTabId);
          if (activeTabData) {
            const tabData = loadTabFromSession(storedActiveTabId);
            if (tabData && tabData.editableData) {
              dispatch(setEditableData(tabData.editableData));
            }
          }
        } else if (allTabEditableData[0]) {
          // Use first tab as fallback
          dispatch(setEditableData(allTabEditableData[0]));
        }
        
        // Mark steps as completed
        dispatch(setStepsCompleted(true));
        setShowRestoreButton(false);
        
        // Use toast to notify user
        const { default: toast } = await import('react-hot-toast');
        toast.success('Data restored successfully from saved session');
        return;
      }
      
      // If no tab data, try the optimized workflow-specific restore function This checks job status via API and handles stream restoration if needed
      const restoreResult = await restoreWorkflowData({
        workflowSessionId: activeWorkflowSessionId,
        dispatch,
        setExtractedData,
        setStepsCompleted,
        setOcrInputID,
        setShowRestoreButton
      });
      
      // If job is still processing, restore the stream
      if (restoreResult.shouldRestoreStream && restoreResult.jobId) {
        // Set the jobId to trigger stream restoration
        setActiveJobId(restoreResult.jobId);
        // Set processing state to show modal
        setIsProcessing(true);
        // Clear modalClosed flag so modal can show
        if (activeWorkflowSessionId) {
          dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
            modalClosed: false,
            isProcessing: true,
          }));
        }
      }
      
      hasManuallyRestoredRef.current = true;
    } catch (error) {
      console.error('Error restoring data:', error);
      const { default: toast } = await import('react-hot-toast');
      toast.error('Failed to restore data. Please try again.');
    }
  };



  useEffect(() => {
    if (backtoDashboardClicked && scrollToID) {
      let retryCount = 0;
      const maxRetries = 20; // Maximum 2 seconds of retries (20 * 100ms)
      
      // Wait for the element to be rendered before scrolling
      const scrollToElement = () => {
        const element = document.getElementById(scrollToID);
        
        if (element && element instanceof Element) {
          try {
            // In demo mode, use demo-container; otherwise use analysis-report
            const containerId = getScrollContainerId(isDemo);
            const container = document.getElementById(containerId);
            const shouldUseNativeScroll = !container || !(container instanceof Element);
            
            if (shouldUseNativeScroll) {
              // Use native scrollIntoView (safer when container doesn't exist)
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // Apply offset manually
              window.scrollBy(0, -150);
            } else {
              // Use react-scroll with the appropriate container
              scroller.scrollTo(scrollToID, {
                duration: 100,     
                offset: -150,
                smooth: 'easeInOutQuint',
                containerId: containerId
              });
            }
          } catch (error) {
            console.warn('Error scrolling to element:', error);
            // Fallback to native scrollIntoView on error
            try {
              if (element && element instanceof Element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                window.scrollBy(0, -150);
              }
            } catch (fallbackError) {
              console.error('Fallback scroll also failed:', fallbackError);
            }
          }
        } else if (retryCount < maxRetries) {
          // If element doesn't exist yet, try again after a short delay
          retryCount++;
          setTimeout(scrollToElement, 100);
        } else {
          console.warn(`Scroll target element with ID '${scrollToID}' not found after ${maxRetries} retries.`);
        }
      };
      
      // Use setTimeout to ensure DOM has updated
      setTimeout(scrollToElement, 100);
    }
    setBacktoDashboardClicked(false);
  }, [backtoDashboardClicked, scrollToID, setBacktoDashboardClicked]);

  useEffect(() => {
    if (stepsCompleted) {
      let retryCount = 0;
      const maxRetries = 20; // Maximum 2 seconds of retries (20 * 100ms)
      
      // Wait for the element to be rendered before scrolling
      const scrollToElement = () => {
        const element = document.getElementById('review-extracted-data');
        
        if (element && element instanceof Element) {
          try {
            // In demo mode, use demo-container; otherwise use analysis-report
            const containerId = getScrollContainerId(isDemo);
            const container = document.getElementById(containerId);
            const shouldUseNativeScroll = !container || !(container instanceof Element);
            
            if (shouldUseNativeScroll) {
              // Use native scrollIntoView (safer when container doesn't exist)
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // Apply offset manually
              window.scrollBy(0, -150);
            } else {
              // Use react-scroll with the appropriate container
              scroller.scrollTo('review-extracted-data', {
                duration: 100,     
                offset: -150,
                smooth: 'easeInOutQuint',
                containerId: containerId
              });
            }
          } catch (error) {
            console.warn('Error scrolling to review-extracted-data:', error);
            // Fallback to native scrollIntoView on error
            try {
              if (element && element instanceof Element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                window.scrollBy(0, -150);
              }
            } catch (fallbackError) {
              console.error('Fallback scroll also failed:', fallbackError);
            }
          }
        } else if (retryCount < maxRetries) {
          // If element doesn't exist yet, try again after a short delay
          retryCount++;
          setTimeout(scrollToElement, 100);
        } else {
          console.warn(`Scroll target element with ID 'review-extracted-data' not found after ${maxRetries} retries.`);
        }
      };
      
      // Use setTimeout to ensure DOM has updated
      setTimeout(scrollToElement, 100);
    }
  }, [stepsCompleted]);

  const handleResetProcessing = () => {
    setIsProcessing(false);
  };

  const handleGenerateTaric = async () => {
    // In demo mode, just show success modal without calling API
    if (isDemo) {
      setIsSuccessfulModalOpen(true);
      return;
    }
    
    // Regular mode - call the actual API
    const result = await generateTaricDeclaration(preparedJson);
    if (result.success) {
      setIsSuccessfulModalOpen(true);
      refetchCreditStats();
    } else {
      setIsSuccessfulModalOpen(true);
    }
  };

  const handleReset = async () => {
    await resetDashboard();
  }

  // Track if tabs have been loaded from sessionStorage
  const hasLoadedTabsFromStorageRef = useRef(false);
  // Track if user has manually restored data (prevents auto-restore)
  const hasManuallyRestoredRef = useRef(false);
  
  // DON'T auto-load tabs from sessionStorage on mount - wait for user to click restore button This prevents automatic data restoration on page refresh

  // Get editableData from Redux state directly (not from tab selector) for migration
  const editableDataFromRedux = useSelector(state => state.documents.editableData);
  
  // Migrate array-based state to tabs when extractedData is set Only migrate if tabs don't exist AND we haven't migrated yet CRITICAL: Store editableData in workflow session state (all declarations together, NOT split into tabs) Tabs are only for UI viewing (TabBar), not for storage Use refs to track last stored data to prevent infinite loops
  const lastStoredEditableDataRef = useRef(null);
  const lastStoredWorkflowSessionIdRef = useRef(null);
  
  
  // PERFORMANCE: Debounce timer to prevent expensive operations during typing
  const storeEditableDataTimerRef = useRef(null);
  
  useEffect(() => {
    // Clear existing timer
    if (storeEditableDataTimerRef.current) {
      clearTimeout(storeEditableDataTimerRef.current);
    }
    
    // Only store if we have an active workflow session
    if (!activeWorkflowSessionId) {
      lastStoredWorkflowSessionIdRef.current = null;
      lastStoredEditableDataRef.current = null;
      return;
    }
    
    // PERFORMANCE: Debounce expensive operations to 1500ms to prevent lag during typing
    storeEditableDataTimerRef.current = setTimeout(() => {
      // CRITICAL: Skip if we're restoring session - prevents saving wrong workflow's data
      if (isRestoringSessionRef.current) {
        return;
      }
      
      // Get current workflow session state from store directly (not from selector to avoid object reference changes)
      const currentState = store.getState();
      const currentWorkflowSession = currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const currentWorkflowEditableData = currentWorkflowSession?.state?.editableData;
      const currentWorkflowExtractedData = currentWorkflowSession?.state?.extractedData;
      
      // CRITICAL: Only save data if it belongs to the current active workflow Priority: Use workflow's own data from session state > global Redux data (only if workflow is currently processing) This prevents workflow-1's data from being saved to workflow-2's session state
      
      // First, check if workflow already has its own data - if so, use that (don't overwrite with global state)
      if (currentWorkflowEditableData && normalizeWorkflowData(currentWorkflowEditableData).length > 0) {
        // Workflow has its own data, don't overwrite with global Redux state
        return;
      }
      
      // Workflow doesn't have its own data - check if we're currently processing THIS workflow Only use global Redux data if we're actively processing for this specific workflow OR if workflow just completed
      const isCurrentlyProcessing = currentState?.workflowSessions?.currentlyProcessingSessionId === activeWorkflowSessionId;
      const workflowIsProcessing = currentWorkflowSession?.state?.isProcessing === true;
      const workflowStepsCompleted = currentWorkflowSession?.state?.stepsCompleted === true;
      
      // Only use global Redux data if this workflow is actually processing OR just completed Otherwise, global Redux might contain data from another workflow
      if (!isCurrentlyProcessing && !workflowIsProcessing && !workflowStepsCompleted) {
        // Not processing and not completed, don't save global Redux data (it might be from another workflow)
        return;
      }
      
      // Workflow is processing - safe to use global Redux data
      const dataToStore = editableDataFromRedux || extractedData;
      const normalizedData = normalizeWorkflowData(dataToStore);
      
      if (normalizedData.length === 0) {
        return;
      }

      // OPTIMIZED: Use shared lightweight hash function
      const normalizedDataHash = createEditableDataHash(normalizedData);
      const lastStoredHash = typeof lastStoredEditableDataRef.current === 'string' && lastStoredEditableDataRef.current.includes('_') 
        ? lastStoredEditableDataRef.current 
        : null;
      const isSameWorkflow = lastStoredWorkflowSessionIdRef.current === activeWorkflowSessionId;
      
      // If same workflow and same hash, skip update (prevents infinite loops)
      if (isSameWorkflow && normalizedDataHash === lastStoredHash) {
        return;
      }

      // Only do expensive JSON.stringify as final check if hash suggests change
      const currentHash = createEditableDataHash(currentWorkflowEditableData);
      if (currentHash === normalizedDataHash) {
        // Hash matches - likely no change, skip expensive stringify
        lastStoredEditableDataRef.current = normalizedDataHash;
        lastStoredWorkflowSessionIdRef.current = activeWorkflowSessionId;
        return;
      }

      // Hash differs - do full comparison with JSON.stringify (expensive, but rare)
      const normalizedDataStr = JSON.stringify(normalizedData);
      const currentEditableDataStr = currentWorkflowEditableData ? JSON.stringify(currentWorkflowEditableData) : null;

      // Only update if data has actually changed from workflow session state
      if (currentEditableDataStr !== normalizedDataStr) {
        // Update ref BEFORE dispatch to prevent re-triggering
        lastStoredEditableDataRef.current = normalizedDataHash; // Store lightweight hash for future comparisons
        lastStoredWorkflowSessionIdRef.current = activeWorkflowSessionId;
        
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
          editableData: normalizedData,
          extractedData: normalizedData, // Also update extractedData in workflow session
        }));
      } else {
        // Data matches workflow session, update refs to prevent future unnecessary checks
        lastStoredEditableDataRef.current = normalizedDataHash; // Use lightweight hash for future comparisons
        lastStoredWorkflowSessionIdRef.current = activeWorkflowSessionId;
      }
    }, 1500); // Debounce to 1500ms to prevent expensive operations during typing
    
    return () => {
      if (storeEditableDataTimerRef.current) {
        clearTimeout(storeEditableDataTimerRef.current);
      }
    };
  }, [activeWorkflowSessionId, editableDataFromRedux, extractedData, dispatch, store, isRestoringSessionRef]);

  // Track if we've saved tabs to sessionStorage after migration
  const hasSavedTabsToStorageRef = useRef(false);

  // IMPORTANT: Save tabs to sessionStorage immediately after migration/creation This ensures data is persisted even if modal closes before debounced save runs
  useEffect(() => {
    // Only save once when tabs are first created (hasTabs changes from false to true) Skip if we've already saved these tabs
    if (hasTabs && tabs.length > 0 && !hasSavedTabsToStorageRef.current) {
      // Save all tabs to sessionStorage immediately (not just active tab)
      tabs.forEach(tab => {
        if (tab.editableData) {
          saveTabToSession(tab.tabId, {
            editableData: tab.editableData,
            selectedAgent: tab.selectedAgent,
            selectedSender: tab.selectedSender,
            selectedReceiver: tab.selectedReceiver,
            validationErrors: tab.validationErrors,
            taricJsonErrors: tab.taricJsonErrors,
            isDirty: tab.isDirty,
            status: tab.status,
          });
        }
      });
      
      // Save active tab ID (null means "All" view)
      if (activeTabId !== null && activeTabId !== undefined) {
        saveActiveTabId(activeTabId);
      } else {
        // Save null to indicate "All" view
        saveActiveTabId(null);
      }
      
      // Save metadata immediately
      const tabsWithData = tabs.filter(tab => 
        tab.editableData && 
        (Array.isArray(tab.editableData) 
          ? tab.editableData.length > 0 
          : Object.keys(tab.editableData || {}).length > 0)
      );
      
      if (tabsWithData.length > 0) {
        const metadata = tabsWithData.map(tab => ({
          tabId: tab.tabId,
          declarationIndex: tab.declarationIndex,
          label: tab.label,
          status: tab.status,
          createdAt: tab.createdAt,
        }));
        saveTabMetadata(metadata);
      }
      
      hasSavedTabsToStorageRef.current = true;
    }
    
    // Reset flag if tabs are cleared
    if (!hasTabs && hasSavedTabsToStorageRef.current) {
      hasSavedTabsToStorageRef.current = false;
    }
  }, [hasTabs, tabs, activeTabId]); // Trigger when tabs are created (hasTabs changes from false to true)

  // Update PDFs with tabId when tabs are created (if PDFs were uploaded before tabs existed)
  useEffect(() => {
    if (activeTabId && tabs.length > 0) {
      // Get the active tab's label/name for tabName
      const activeTab = tabs.find(t => t.tabId === activeTabId);
      // PDFs are now stored only in memory (workflow file cache) - no tabId association needed
    }
  }, [activeTabId, tabs]);
  
  // DON'T auto-restore data from tabs - wait for user to click restore button

  // Sync extractedData from tabs after stream finishes (tabs are source of truth) CRITICAL: Only sync if we're NOT restoring session (prevents cross-contamination) Only update extractedData, not editableData (editableData comes from selector)
  useEffect(() => {
    // CRITICAL: Skip if restoring session - prevents syncing wrong workflow's data
    if (isRestoringSessionRef.current) {
      return;
    }
    
    // Only sync if: tabs exist, stream is finished, and we have tab data CRITICAL: Also check that this workflow is currently processing or has data This prevents workflow-1's tab data from being synced to workflow-2
    if (hasTabs && tabs.length > 0 && stepsCompleted && !isStreamActiveRef.current) {
      // Verify that tabs belong to current workflow by checking if workflow is processing or has data
      const isCurrentlyProcessing = workflowSessions?.currentlyProcessingSessionId === activeWorkflowSessionId;
      const workflowIsProcessing = activeWorkflowSession?.state?.isProcessing === true;
      const workflowHasData = hasWorkflowData(activeWorkflowSession?.state);
      
      // Only sync if workflow is processing or has its own data
      if (isCurrentlyProcessing || workflowIsProcessing || workflowHasData) {
        const allTabEditableData = tabs.map(tab => tab.editableData).filter(Boolean);
        if (allTabEditableData.length > 0) {
          // Only update if different to avoid loops
          const currentExtractedDataStr = JSON.stringify(extractedData);
          const allTabEditableDataStr = JSON.stringify(allTabEditableData);
          
          if (currentExtractedDataStr !== allTabEditableDataStr) {
            dispatch(setExtractedData(allTabEditableData));
          }
        }
      }
    }
  }, [hasTabs, tabs, stepsCompleted, extractedData, dispatch, activeJobId, activeWorkflowSessionId, workflowSessions, activeWorkflowSession, isRestoringSessionRef]);

  // Save active tab data to sessionStorage when it changes
  useEffect(() => {
    if (activeTabId && activeTabData) {
      // Debounce sessionStorage writes
      const timeoutId = setTimeout(() => {
        saveTabToSession(activeTabId, activeTabData);
        saveActiveTabId(activeTabId);
        
        // Save metadata - only save tabs that have editableData (tabs with actual data)
        if (tabs.length > 0) {
          const tabsWithData = tabs.filter(tab => 
            tab.editableData && 
            (Array.isArray(tab.editableData) 
              ? tab.editableData.length > 0 
              : Object.keys(tab.editableData || {}).length > 0)
          );
          
          // Only save metadata for tabs with data
          if (tabsWithData.length > 0) {
            const metadata = tabsWithData.map(tab => ({
              tabId: tab.tabId,
              declarationIndex: tab.declarationIndex,
              label: tab.label,
              status: tab.status,
              createdAt: tab.createdAt,
            }));
            saveTabMetadata(metadata);
          }
        }
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [activeTabId, activeTabData, tabs]);

  // Set default agent (first agent) for workflow session when agents and declarations are available CRITICAL: Use workflow session's selectedAgent array, not tabs
  const setDefaultAgentTimerRef = useRef(null);
  
  useEffect(() => {
    // Clear existing timer
    if (setDefaultAgentTimerRef.current) {
      clearTimeout(setDefaultAgentTimerRef.current);
    }
    
    if (!activeWorkflowSessionId || !agents || !Array.isArray(agents) || agents.length === 0) {
      return;
    }
    
    const firstAgent = agents[0];
    if (!firstAgent || typeof firstAgent !== 'object') {
      return;
    }
    
    // Debounce to prevent multiple updates during initialization
    setDefaultAgentTimerRef.current = setTimeout(() => {
      // Get current workflow session state
      const currentState = store.getState();
      const currentWorkflowSession = currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const currentSelectedAgents = currentWorkflowSession?.state?.selectedAgent || [];
      
      // Check if we need to set default agent for any declaration index Get editableData length to know how many declarations we have
      const editableDataFromWorkflow = currentWorkflowSession?.state?.editableData;
      const declarationsCount = editableDataFromWorkflow 
        ? (Array.isArray(editableDataFromWorkflow) ? editableDataFromWorkflow.length : 1)
        : 0;
      
      // Only set default if we have declarations but no agents selected
      if (declarationsCount > 0) {
        let needsUpdate = false;
        const updatedAgents = [...currentSelectedAgents];
        
        // Ensure array is large enough for all declarations
        while (updatedAgents.length < declarationsCount) {
          updatedAgents.push(null);
        }
        
        // Set first agent as default for any declaration that doesn't have an agent
        for (let i = 0; i < declarationsCount; i++) {
          if (!updatedAgents[i] || typeof updatedAgents[i] !== 'object') {
            updatedAgents[i] = firstAgent;
            needsUpdate = true;
          }
        }
        
        // Update workflow session state if needed
        if (needsUpdate) {
          dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
            selectedAgent: updatedAgents,
          }));
        }
      }
    }, 500); // Debounce to prevent multiple updates
    
    return () => {
      if (setDefaultAgentTimerRef.current) {
        clearTimeout(setDefaultAgentTimerRef.current);
      }
    };
  }, [activeWorkflowSessionId, agents, dispatch, store, editableData, extractedData]);

  // Track last generated data to prevent unnecessary re-runs
  const lastTaricGenerationRef = useRef(null);
  
  // CRITICAL: Track saved editableData hash (only updates when Save is clicked, not during editing) This ensures TARIC JSON only regenerates after save, not on every keystroke
  const lastSavedEditableDataHashRef = useRef(null);
  const previousIsDirtyRef = useRef(null);
  
  // Get isDirty state to detect when save happens (isDirty goes from true to false) Use existing activeTabData from line 136
  const activeTabIsDirty = activeTabData?.isDirty || false;
  const globalIsDirty = useSelector(state => state.documents.isDirty || false);
  const finalIsDirty = hasTabs ? activeTabIsDirty : globalIsDirty;
  
  // Detect when save happens (isDirty goes from true to false)
  useEffect(() => {
    if (previousIsDirtyRef.current === true && finalIsDirty === false) {
      // Save just happened - clear the saved hash so next generation will use new data
      lastSavedEditableDataHashRef.current = null;
    }
    previousIsDirtyRef.current = finalIsDirty;
  }, [finalIsDirty, hasTabs]);
  
  // CRITICAL: Create selectors for workflow session selections to trigger TARIC JSON regeneration When selections change, we need to regenerate the JSON Extract IDs to detect actual changes (not just reference changes)
  const workflowSelectedSenders = activeWorkflowSession?.state?.selectedSender || [];
  const workflowSelectedReceivers = activeWorkflowSession?.state?.selectedReceiver || [];
  const workflowSelectedAgents = activeWorkflowSession?.state?.selectedAgent || [];
  
  // Create stable dependencies that change when selections actually change Extract IDs from selections to compare by value, not by reference
  const workflowSelectionIds = JSON.stringify({
    senderIds: workflowSelectedSenders.map(s => s?.id || null).filter(id => id !== null).sort(),
    receiverIds: workflowSelectedReceivers.map(r => r?.id || null).filter(id => id !== null).sort(),
    agentIds: workflowSelectedAgents.map(a => a?.id || null).filter(id => id !== null).sort(),
  });

  useEffect(() => {
      // Early return if no workflow session ID
      if (!activeWorkflowSessionId) {
        return;
      }
      
      // Validate validationErrors - check for actual nested errors
      let hasActualValidationErrors = false;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        hasActualValidationErrors = validationErrors.some(declaration => {
          if (!declaration || typeof declaration !== 'object') return false;
          if (declaration.declarationErrors && Array.isArray(declaration.declarationErrors) && declaration.declarationErrors.length > 0) return true;
          if (declaration.itemErrors && Array.isArray(declaration.itemErrors) && declaration.itemErrors.length > 0) {
            return declaration.itemErrors.some(item => item.errors && Array.isArray(item.errors) && item.errors.length > 0);
          }
          if (declaration.documentErrors && Array.isArray(declaration.documentErrors) && declaration.documentErrors.length > 0) {
            return declaration.documentErrors.some(doc => doc.errors && Array.isArray(doc.errors) && doc.errors.length > 0);
          }
          if (declaration.packageErrors && Array.isArray(declaration.packageErrors) && declaration.packageErrors.length > 0) {
            return declaration.packageErrors.some(pkg => pkg.errors && Array.isArray(pkg.errors) && pkg.errors.length > 0);
          }
          return false;
        });
      }
      
      if (hasActualValidationErrors) {
        return;
      }
      
      let validSelectedSenders = [];
      let validSelectedReceivers = [];
      let validSelectedAgents = [];
      let declarationsToUse = [];
      
      // CRITICAL: For TARIC JSON generation, use extractedData (saved state) NOT editableData (editing state) This ensures JSON only regenerates after save, not during typing Use active workflow session's data (all declarations together, not split by tabs)
      const currentState = store.getState();
      const currentWorkflowSession = currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId];
      
      if (currentWorkflowSession?.state) {
        const workflowState = currentWorkflowSession.state;
        
        // CRITICAL: Use extractedData (saved state) for TARIC JSON generation, not editableData (editing state) Priority: Redux extractedData (always updated on save) > workflow session extractedData > workflow session editableData This ensures we always use the most recent saved state
        const reduxExtractedData = normalizeWorkflowData(extractedData);
        const workflowExtractedData = normalizeWorkflowData(workflowState.extractedData);
        const workflowEditableData = normalizeWorkflowData(workflowState.editableData);
        // Use Redux extractedData first (always up-to-date after save), then fallback to workflow session data
        declarationsToUse = reduxExtractedData.length > 0 ? reduxExtractedData : 
                           (workflowExtractedData.length > 0 ? workflowExtractedData : workflowEditableData);
        
        // Get selected arrays from workflow session
        validSelectedSenders = Array.isArray(workflowState.selectedSender) ? workflowState.selectedSender : [];
        validSelectedReceivers = Array.isArray(workflowState.selectedReceiver) ? workflowState.selectedReceiver : [];
        validSelectedAgents = Array.isArray(workflowState.selectedAgent) ? workflowState.selectedAgent : [];
        
        // Filter to only complete declarations (those with both sender AND receiver)
        const completeDeclarations = [];
        const completeSenders = [];
        const completeReceivers = [];
        const completeAgents = [];
        
        declarationsToUse.forEach((declaration, index) => {
          const hasSender = validSelectedSenders[index] && typeof validSelectedSenders[index] === 'object';
          const hasReceiver = validSelectedReceivers[index] && typeof validSelectedReceivers[index] === 'object';
          
          if (hasSender && hasReceiver) {
            completeDeclarations.push(declaration);
            completeSenders.push(validSelectedSenders[index]);
            completeReceivers.push(validSelectedReceivers[index]);
            completeAgents.push(validSelectedAgents[index] || null);
          }
        });
        
        declarationsToUse = completeDeclarations;
        validSelectedSenders = completeSenders;
        validSelectedReceivers = completeReceivers;
        validSelectedAgents = completeAgents;
      } else {
        // No workflow session: Use global extractedData and arrays (backward compatibility)
        declarationsToUse = normalizeWorkflowData(extractedData);
        if (declarationsToUse.length === 0) {
          return;
        }
        
        validSelectedSenders = Array.isArray(selectedSenders) ? selectedSenders : [];
        validSelectedReceivers = Array.isArray(selectedReceivers) ? selectedReceivers : [];
        validSelectedAgents = Array.isArray(selectedAgents) ? selectedAgents : [];
      }
    
    // Generate TARIC JSON if we have at least one complete declaration (with sender and receiver)
    const hasCompleteData = declarationsToUse.length > 0 && 
                           validSelectedSenders.length > 0 && 
                           validSelectedReceivers.length > 0 &&
                           validSelectedSenders.length === validSelectedReceivers.length &&
                           validSelectedSenders.length === declarationsToUse.length;
    
    // Create a signature to check if we've already generated for this data CRITICAL: Include actual selection IDs (not just counts) to detect when selections change
    const senderIds = validSelectedSenders.map(s => s?.id || null).filter(id => id !== null);
    const receiverIds = validSelectedReceivers.map(r => r?.id || null).filter(id => id !== null);
    const agentIds = validSelectedAgents.map(a => a?.id || null).filter(id => id !== null);
    
    // CRITICAL: Create a comprehensive hash of editableData to detect actual data changes This hash includes all key fields that affect TARIC JSON generation (more detailed than lightweight hash)
    const createTaricDataHash = (data) => {
      if (!data || data.length === 0) return '';
      // Create a hash based on key fields that affect TARIC JSON generation Include all important declaration-level fields
      try {
        return data.map(d => {
          if (!d || typeof d !== 'object') return '';
          // Include all key fields that affect the JSON structure Use JSON.stringify for consistent hashing
          return JSON.stringify({
            expediente: d.expediente || '',
            tipo: d.tipo || null,
            factura: d.factura || '',
            fecha_factura: d.fecha_factura || '',
            clave_declarante: d.clave_declarante || '',
            contacto_telefono: d.contacto_telefono || '',
            flag_datos_seguridad: d.flag_datos_seguridad || null,
            fecha_registro: d.fecha_registro || null,
            observaciones: d.observaciones || null,
            // Include a hash of partidas to detect item changes
            partidasCount: d.partidas && Array.isArray(d.partidas) ? d.partidas.length : 0,
            partidasHash: d.partidas && Array.isArray(d.partidas) 
              ? d.partidas.map(p => `${p?.codigo_nc || ''}:${p?.masa_bruta || ''}:${p?.precio || ''}`).join('|')
              : '',
          });
        }).join('||');
      } catch (error) {
        console.error('Error creating TARIC data hash:', error);
        return '';
      }
    };
    
    const editableDataHash = createTaricDataHash(declarationsToUse);
    
    // CRITICAL: Only regenerate if data was saved (lastSavedEditableDataHashRef is null) lastSavedEditableDataHashRef is cleared when save happens (isDirty goes false) We do NOT regenerate just because hash changed - only when save happens
    const savedEditableDataHash = lastSavedEditableDataHashRef.current;
    const hasDataChangedSinceLastSave = savedEditableDataHash === null;
    
    const generationSignature = JSON.stringify({
      declarationsCount: declarationsToUse.length,
      senderIds: senderIds.sort(), // Sort to ensure consistent comparison
      receiverIds: receiverIds.sort(),
      agentIds: agentIds.sort(),
      editableDataHash, // Use current hash
      hasErrors: hasActualValidationErrors,
      workflowId: activeWorkflowSessionId,
      workflowSelectionIds, // Include selection IDs for comparison
    });
    
    // CRITICAL: Only regenerate TARIC JSON when: 1. We have complete data (all declarations have sender and receiver) 2. Either: - We haven't generated yet (first time), OR - Selections changed (workflowSelectionIds changed), OR - Data was saved (isDirty went false, so lastSavedEditableDataHashRef is null)
    let hasSelectionsChanged = false;
    if (lastTaricGenerationRef.current !== null) {
      try {
        const lastSig = JSON.parse(lastTaricGenerationRef.current);
        const lastSelectionIds = lastSig.workflowSelectionIds || workflowSelectionIds;
        hasSelectionsChanged = lastSelectionIds !== workflowSelectionIds;
      } catch (e) {
        // If parsing fails, assume selections changed (safer to regenerate)
        hasSelectionsChanged = true;
      }
    }
    
    const shouldRegenerate = hasCompleteData && 
                            (lastTaricGenerationRef.current === null || 
                             hasSelectionsChanged || 
                             hasDataChangedSinceLastSave);
    
    // CRITICAL: Double-check - if hash changed but save didn't happen, skip regeneration (editing in progress) This prevents regeneration during typing even if effect runs due to other dependencies
    if (!hasDataChangedSinceLastSave && savedEditableDataHash !== null && savedEditableDataHash !== editableDataHash) {
      // Hash changed but save didn't happen - skip regeneration (editing in progress)
      return;
    }
    
    if (shouldRegenerate) {
      // Update last saved hash when regenerating (this represents the "saved" state) This prevents regeneration during editing (when hash changes but not saved yet)
      lastSavedEditableDataHashRef.current = editableDataHash;
      lastTaricGenerationRef.current = generationSignature;
      
      try {
        dispatch(prepareTaricData({
          selectedSenders: validSelectedSenders,
          selectedReceivers: validSelectedReceivers,
          selectedAgents: validSelectedAgents,
          userData: userData && typeof userData === 'object' ? userData : null,
          extractedData: declarationsToUse, // Use saved state (Redux extractedData or workflow session extractedData) for TARIC JSON generation
          company: company && typeof company === 'object' ? company : null
        }));
        dispatch(generateTaricJson());
        
        // CRITICAL: Sync preparedJson to workflow session state after generation We need to wait a bit for the reducer to finish updating the global preparedJson state
        setTimeout(() => {
          const updatedState = store.getState();
          const updatedPreparedJson = updatedState.preparedJson;
          if (updatedPreparedJson && activeWorkflowSessionId) {
            dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
              preparedJson: updatedPreparedJson,
            }));
          }
        }, 100);
        
        // Only refetch credits after successful generation, not on every data change Credits will be refetched after actual export/generation completes
      } catch (error) {
        console.error('Error generating TARIC JSON:', error);
      }
    } else if (!hasCompleteData) {
      // Reset ref when data is incomplete
      lastTaricGenerationRef.current = null;
    }
  }, [
    activeWorkflowSessionId, 
    // CRITICAL: Include workflow session selection IDs to trigger regeneration when selections change Use ID-based signature to detect actual value changes, not just reference changes
    workflowSelectionIds,
    // CRITICAL: Include isDirty state to detect when save happens (isDirty goes false) When save happens, lastSavedEditableDataHashRef is cleared, triggering regeneration
    finalIsDirty,
    // NOTE: Do NOT include editableData or extractedData in dependencies - we only regenerate after save, not during editing The effect reads data directly from workflow session state inside the effect, not from dependencies Fallback dependencies for backward compatibility (when no workflow session)
    selectedSenders, 
    selectedReceivers, 
    selectedAgents, 
    // REMOVED: extractedData - causes regeneration on every keystroke, breaking performance
    validationErrors, 
    dispatch, 
    userData, 
    company, 
    store
  ]);

  return (
    <div className="m-0 text-left pb-40">
      <MultiProcessingModalManager
          isProcessing={isProcessing}
          setIsProcessing={setIsProcessing}
          setStepsCompleted={setStepsCompleted}
          onProcessComplete={(onStatusUpdate) =>
            isDemo
              ? processDocuments(
                  documents,
                  onStatusUpdate,
                  companyFormData.emailAddress,
                  specifyAmount,
                  additionalContext,
                  companyFormData.companyName
                )
              : processDocuments(documents, onStatusUpdate, specifyAmount, additionalContext)
          }
          onCancelProcessing={() => setProcessingCancelled(true)}
        />
      {isSuccessfulModalOpen && (
        isDemo ? (
          <DemoGeneratedSuccessfullyModal
            setIsSuccessfulModalOpen={setIsSuccessfulModalOpen}
            setIsProcessing={handleResetProcessing}
            setIsReviewConfirmed={setIsReviewConfirmed}
            extractedData={extractedData}
            activeWorkflowSessionId={activeWorkflowSessionId}
            workflowSessions={workflowSessions}
          />
        ) : (
          <GeneratedSuccessfullyModal
            setIsSuccessfulModalOpen={setIsSuccessfulModalOpen}
            setIsProcessing={handleResetProcessing}
            setIsReviewConfirmed={setIsReviewConfirmed}
            hasError={hasGenerationError}
            errorMessage={generationErrorMessage}
            taricJsonErrors={taricJsonErrors}
            onRetry={handleGenerateTaric}
          />
        )
      )}
      
      {/* Rate Limit Exceeded Modal - Only show in demo mode */}
      {isDemo && (
        <RateLimitExceededModal
          isOpen={rateLimitExceeded}
          onClose={() => setRateLimitExceeded(false)}
          rateLimit={rateLimit}
        />
      )}
      <div className="mt-10">
        <h1 className="text-5xl font-bold mb-1">{getTranslation('welcome', isSpanish)} {userData?.first_name || "Atlantis Digital"} 👋,</h1>
        <p className="text-gray-500 mt-3">{getTranslation('streamline', isSpanish)}</p>
      </div>

       {/* Company Information Section */}
       <div className="mt-16 w-full">
        <CompanyInformation formData={companyFormData} handleChange={handleCompanyFormChange} />
      </div>

      {isCompanyFormComplete && (
        <>

         {/* Workflow Session Tabs - for managing multiple workflows */}
      <WorkflowTabBar />
    
        {(!loading && current && current.remainingCredits === 0) ? (
          <div className="flex flex-col font-sans bg-red-50 rounded-xl p-6 mt-20 w-full relative overflow-hidden max-w-2xl">
          <div className="z-1 text-lg font-bold text-red-600 mb-2">
            {getTranslation('noCreditsLeft', isSpanish) || 'You have used up all your remaining credits!'}
          </div>
          <div className="z-1 text-sm text-red-500 mb-4">
            {getTranslation('pleaseBuyCredits', isSpanish) || 'Please buy more credits to continue using the service.'}
          </div>
          <button
            className="z-1 mt-4  w-fit text-center h-10 gap-2 py-2 px-5 rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold"
            onClick={() => setIsStripeModalOpen(true)}
          >
            {getTranslation('buyCredits', isSpanish)}
          </button>
          <img
            src={star}
            alt="Star"
            className="absolute bottom-[-2rem] right-4 size-44 opacity-100 pointer-events-none select-none"
            aria-hidden="true"
          />
        </div>
        ) : (
          <div className="bg-hoverBg rounded-md mt-16 w-full">
          <UploadDocuments />
          </div>
        )}
     
     
      {hasDocuments && uploadComplete && !extractedData && uploadedFiles.length > 0 && !isProcessing && (
        <div className="mt-4 space-y-3">
          <DUASpecification 
            onSpecifyAmountChange={setSpecifyAmount}
            onAdditionalContextChange={setAdditionalContext}
          />
          <div className="flex gap-2 justify-start !mt-8">
            {(() => {
              // Disable if the ACTIVE workflow is currently processing (not other workflows) This allows other workflows to be queued even if another workflow is processing Only disable if this specific workflow is processing
              const shouldDisable = isProcessing;
              
              return (
            <button
                  className="py-2 px-5 rounded-full bg-default border flex gap-2 items-center disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={async () => {
                // REMOVED: No longer checking if another workflow is processing Backend supports multiple concurrent jobs - all workflows can process simultaneously
                dispatch(setCurrentlyProcessingSession(activeWorkflowSessionId));
                setIsProcessing(true);
                dispatch(toggleCollapseFiles());
              }}
                  disabled={shouldDisable}
            >
              <FileCheck className="size-5 stroke-white"/>
              <span className='text-white'>{getTranslation('confirmAndProcess', isSpanish)}</span>
            </button>
              );
            })()}
          </div>
        </div>
      )}

      {showRestoreButton && !extractedData && (
        <div className="flex gap-2 mt-4">
          <button
            className="py-2 px-5 rounded-full bg-default border flex gap-2 items-center"
            onClick={handleRestoreData}
          >
            <FileCheck className="size-5 stroke-white"/>
            <span className='text-white'>{getTranslation('restore', isSpanish) || 'Restore Processed Data'}</span>
          </button>
        </div>
      )}

      {stepsCompleted && (
        <>
          <ReviewExtractedData />
          <ReviewUserDetails userData={userData} navigate={navigate} />

          {/* Tabs for each export/declaration */}
          {hasTabs && tabs.length > 0 ? (
            <div className="mt-8">
              {/* Tab Navigation */}
              <TabBar isSpanish={isSpanish} />

              {/* Content - show all tabs if "All" is selected, or just active tab if specific tab is selected */}
                <div className="space-y-8">
                {(() => {
                  // If no activeTabId is set, show all tabs (default "All" view) Otherwise, show only the active tab
                  const tabsToShow = !activeTabId ? tabs : tabs.filter(tab => tab.tabId === activeTabId);
                  
                  return tabsToShow.map((tab) => (
                    <div key={tab.tabId} className="">
                      <h3 className="text-template-primary text-2xl font-semibold mb-4 uppercase">
                        {tab.label || (isSpanish ? `Exportación #${(tab.declarationIndex || 0) + 1}` : `Export #${(tab.declarationIndex || 0) + 1}`)}
                      </h3>
                      <div className='review-container grid gap-4 grid-cols-3 mt-4'>
                      <ReviewAgents 
                          agents={finalAgents} 
                          navigate={navigate} 
                          userData={userData} 
                          tabId={tab.tabId}
                        />
                        <ReviewSenderDetails 
                          contacts={contacts} 
                          userData={userData} 
                          navigate={navigate} 
                          tabId={tab.tabId}
                        />
                        <ReviewReceiverDetails 
                          contacts={contacts} 
                          navigate={navigate} 
                          userData={userData} 
                          tabId={tab.tabId}
                        />
                      </div>
                    </div>
                  ));
                })()}
                </div>
            </div>
          ) : extractedData && !hasTabs && stepsCompleted ? (
            // Fallback: Show array-based view if tabs haven't been created yet
            <div className="mt-8">
              <div className="space-y-8">
                {normalizeWorkflowData(extractedData).map((declaration, index) => (
                  <div key={index} className="">
                <h3 className="text-template-primary text-2xl font-semibold mb-4 uppercase">
                      {isSpanish ? `Exportación #${index + 1}` : `Export #${index + 1}`}
              </h3>
                    <div className='review-container grid gap-4 grid-cols-3'>
                  <ReviewAgents 
                    agents={finalAgents} 
                    navigate={navigate} 
                    userData={userData} 
                        declarationIndex={index}
                  />
                  <ReviewSenderDetails 
                    contacts={contacts} 
                    userData={userData} 
                    navigate={navigate} 
                        declarationIndex={index}
                  />
                  <ReviewReceiverDetails 
                    contacts={contacts} 
                    navigate={navigate} 
                    userData={userData} 
                        declarationIndex={index}
                  />
                </div>
            </div>
                ))}
              </div>
            </div>
          ) : null}

          {(() => {
            // CRITICAL: Check validation errors structure properly Validation errors are an array of objects with nested error arrays: [{ declarationErrors: [], itemErrors: [], packageErrors: [], documentErrors: [] }] We need to check if ANY nested array has errors, not just if the main array has items
            
            let hasActualErrors = false;
            
            if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
              // Check if any declaration has actual errors in nested arrays
              hasActualErrors = validationErrors.some(declaration => {
                if (!declaration || typeof declaration !== 'object') return false;
                
                // Check declaration-level errors
                if (declaration.declarationErrors && Array.isArray(declaration.declarationErrors) && declaration.declarationErrors.length > 0) {
                  return true;
                }
                
                // Check item-level errors
                if (declaration.itemErrors && Array.isArray(declaration.itemErrors) && declaration.itemErrors.length > 0) {
                  // Check if any item has errors
                  return declaration.itemErrors.some(item => 
                    item.errors && Array.isArray(item.errors) && item.errors.length > 0
                  );
                }
                
                // Check document-level errors
                if (declaration.documentErrors && Array.isArray(declaration.documentErrors) && declaration.documentErrors.length > 0) {
                  // Check if any document has errors
                  return declaration.documentErrors.some(doc => 
                    doc.errors && Array.isArray(doc.errors) && doc.errors.length > 0
                  );
                }
                
                // Check package-level errors
                if (declaration.packageErrors && Array.isArray(declaration.packageErrors) && declaration.packageErrors.length > 0) {
                  // Check if any package has errors
                  return declaration.packageErrors.some(pkg => 
                    pkg.errors && Array.isArray(pkg.errors) && pkg.errors.length > 0
                  );
                }
                
                return false;
              });
            }
            
            // Check all conditions for displaying finalJsonData
            const declarations = normalizeWorkflowData(extractedData);
            const hasExtractedData = !!extractedData && declarations.length > 0;
            
            // CRITICAL: Check selections from workflow session first, then fallback to global
            let allHaveSelections = false;
            if (activeWorkflowSessionId && activeWorkflowSession?.state) {
              const workflowState = activeWorkflowSession.state;
              const workflowSelectedSenders = Array.isArray(workflowState.selectedSender) ? workflowState.selectedSender : [];
              const workflowSelectedReceivers = Array.isArray(workflowState.selectedReceiver) ? workflowState.selectedReceiver : [];
              allHaveSelections = declarations.length > 0 && declarations.every((_, index) => 
                workflowSelectedSenders[index] && 
                workflowSelectedReceivers[index] &&
                typeof workflowSelectedSenders[index] === 'object' &&
                typeof workflowSelectedReceivers[index] === 'object'
              );
            } else {
              // Fallback to global selections
              allHaveSelections = declarations.length > 0 && declarations.every((_, index) => 
                selectedSenders[index] && selectedReceivers[index]
              );
            }
            
            return !hasActualErrors;
          })() && (
          
          <div id='json-container'>
            {extractedData && (() => {
              const declarations = normalizeWorkflowData(extractedData);
              
              // Use workflow session state for selections (same logic as generation)
              let shouldShowJson = false;
              
              if (activeWorkflowSessionId && activeWorkflowSession?.state) {
                const workflowState = activeWorkflowSession.state;
                const workflowDeclarations = getWorkflowDeclarations(workflowState);
                const workflowSelectedSenders = Array.isArray(workflowState.selectedSender) ? workflowState.selectedSender : [];
                const workflowSelectedReceivers = Array.isArray(workflowState.selectedReceiver) ? workflowState.selectedReceiver : [];
                
                // Check if all declarations have both sender and receiver
                const allHaveSelections = workflowDeclarations.length > 0 && 
                  workflowDeclarations.every((_, index) => 
                    workflowSelectedSenders[index] && 
                    workflowSelectedReceivers[index] &&
                    typeof workflowSelectedSenders[index] === 'object' &&
                    typeof workflowSelectedReceivers[index] === 'object'
                  );
                
                shouldShowJson = allHaveSelections && !!preparedJson?.taricJson;
              } else {
                // No workflow session: Use global selectedSenders/selectedReceivers (backward compatibility)
                const allHaveSelections = declarations.length > 0 && declarations.every((_, index) => 
                  selectedSenders[index] && selectedReceivers[index]
                );
                shouldShowJson = allHaveSelections && !!preparedJson?.taricJson;
              }
              
              return shouldShowJson;
            })() && (
              <>
                <FinalJsonData jsonData={preparedJson.taricJson} />
                <div className="flex gap-2 mt-4 justify-end">
                <button
                    className="flex items-center gap-2 py-2 px-5 rounded-full bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleReset}
                  >
                    <RotateCcw className="size-5 stroke-red-500"/>
                    <span className='text-red-500'>
                    {getTranslation('reset', isSpanish)}
                    </span>
                  </button>
                  <button
                    className="flex items-center gap-2 py-2 px-5 rounded-full bg-default border disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleGenerateTaric}
                    disabled={isGeneratingTaric}
                  >
                    <FileJson className="size-5 stroke-white"/>
                    <span className='text-white'>
                    {isGeneratingTaric 
                      ? getTranslation('generatingDeclaration', isSpanish)
                      : hasGenerationError 
                        ? getTranslation('retryDeclaration', isSpanish)
                        : getTranslation('generateDeclaration', isSpanish)}
                    </span>
                  </button>
                 
                </div>
              </>
            )}
          </div>

)}
        </>
      )}
          </>
      )}
    </div>
  );
}

export default Dashboard;